
/**
 * Создаёт функцию, которая позволяет запускать callback с динамически изменяемым интервалом.
 *
 * @description
 * Возвращает функцию, которая при вызове:
 * - Обновляет callback, который будет выполняться.
 * - Обновляет интервал (в миллисекундах), если он изменился.
 * - Автоматически перезапускает таймер только при изменении интервала.
 * - Поддерживает стабильное выполнение, даже если вызывается часто.
 *
 * Полезно для сценариев, где нужно регулярно выполнять код (например, в mousemove, scroll),
 * но с возможностью динамически менять частоту выполнения.
 *
 * @example
 * const run = createDynamicInterval();
 *
 * document.addEventListener('mousemove', (e) => {
 *   const interval = e.shiftKey ? 1000 : 100;
 *   run(() => {
 *     console.log('Проверка каждые', interval, 'мс');
 *   }, interval);
 * });
 *
 * @param {Function} callback - Функция, которая будет вызываться по расписанию.
 *                              Должна быть синхронной или возвращать Promise (но не ожидается).
 * @param {number} wait - Интервал в миллисекундах, через который повторять вызов.
 *                        Должен быть положительным числом.
 *
 * @returns {Function} Функция с сигнатурой (callback: VoidFunction, wait: number) => void,
 *                     которая управляет динамическим интервалом выполнения.
 *
 * @throws {TypeError} Если callback не является функцией (во время вызова).
 *
 * @author [Diogo]
 * @since 1.0.0
 *
 * @todo Поддержка остановки/паузы через возвращаемый объект.
 * @todo Поддержка передачи аргументов в callback.
 */
export const createDynamicInterval = () => {
	let timeoutId: ReturnType<typeof setTimeout> | null = null
	let lastCallback: VoidFunction | null = null
	let currentWait = 0

	const loop = () => {
			if (lastCallback) {
					lastCallback()
			}
			timeoutId = setTimeout(loop, currentWait)
	}

	const schedule = (callback: VoidFunction, wait: number) => {
			const needsRestart = timeoutId !== null && wait !== currentWait

			lastCallback = callback
			currentWait = wait

			if (needsRestart) {
					if (timeoutId !== null) {
							clearTimeout(timeoutId)
							timeoutId = null
					}
			}

			if (timeoutId === null) {
					timeoutId = setTimeout(loop, currentWait)
			}
	}

	// Добавляем метод stop
	schedule.stop = () => {
			if (timeoutId !== null) {
					clearTimeout(timeoutId)
					timeoutId = null
			}
			lastCallback = null
			currentWait = 0
	}

	return schedule
}